import {
  mergeWith,
  // orderBy,
  shuffle,
} from 'lodash';

import { StationType } from '../../constants/Games/gameStation';
import { BalanceJson, Icon, PartKey, Uuid } from '../../models/Base';
import {
  GameControllerIntermediate,
  GameControllerIntermediateGameItem,
  PrepareIntermediateFn,
} from '../../models/GameControllerStore';
import {
  GameCollection,
  GameCollectionSlideshow,
  GameType,
  I18nPartInfo,
} from '../../models/GameStore';
import {
  // AlbumChallengeType,
  AlbumContentType,
  // PartCount,
} from '../../models/SpaceStore';
import { useGameStore } from '../../stores/GameStore';
import { useSpaceStore } from '../../stores/SpaceStore';
import { customLog } from '../../utils/logger';
import { useUserStore } from '../UserStore';

// import { useSystemStore } from '../../stores/SystemStore';
import {
  // isGameType,
  isSpeaking,
} from './utils';

export type GameParams = {
  classroomId: string;
  gameSetId: string;
  gameId: string;
  gameCollectionIds?: string[];
  challengeIndex: never;
  // postId?: string;
  // reportCase?: string;
};

export type GameIntermediate = GameControllerIntermediate & {
  gameCollectionId: Uuid;
  partIndex: PartKey;
  i18nPartInfo: I18nPartInfo | undefined;
  icon: Icon | undefined;
  partTheme: number;
  gameType: GameType;
  slideshow?: 'auto' | GameCollectionSlideshow;
  unlockFee: BalanceJson;
  gameReward: BalanceJson;
  gameAnswerSlots: number;
  slideshowReward?: BalanceJson;
};

export const prepareGameIntermediate: PrepareIntermediateFn<
  GameParams,
  GameIntermediate
> = async (_utils, { gameSetId, gameId, gameCollectionIds = [] }) => {
  const { getAlbum } = useSpaceStore.getState();
  const { getGameCollection, fetchGameCollectionsByIds } =
    useGameStore.getState();

  let gameCollections;

  // assuming game set already downloaded
  const album = getAlbum(gameSetId);

  const content = album?.content;
  if (
    content === undefined ||
    content === null ||
    ![AlbumContentType.Both, AlbumContentType.Game].includes(content.type)
  ) {
    throw new Error('invalid content type');
  }

  // download all game collection in gameCollectionIds first
  if (gameCollectionIds.length !== content.gameCollectionIds.length) {
    await fetchGameCollectionsByIds({
      fieldName: 'gamePlay',
      gameCollectionIds: content.gameCollectionIds,
    });
    return { gameCollectionIds: content.gameCollectionIds };
  }

  gameCollections = gameCollectionIds.map(getGameCollection);

  // throw if downloaded game collection but cannot get from repository
  if (gameCollections.some((gameCollection) => !gameCollection)) {
    throw new Error('game collection not found');
  }
  gameCollections = gameCollections as GameCollection[];

  // find game items within the same part
  const items = mergeWith(
    {},
    ...gameCollections.map((gameCollection) =>
      gameCollection.getPartItemMap(({ game }) => game !== undefined),
    ),
    (
      objValue: GameControllerIntermediateGameItem[],
      srcValue: GameControllerIntermediateGameItem[],
    ) => (Array.isArray(objValue) ? objValue.concat(srcValue) : undefined),
  ) as Record<string, GameControllerIntermediateGameItem[]>;

  // console.log('source');
  // console.log(JSON.stringify(items));

  const [partIndex, partGameItems] =
    (
      Object.entries(items) as [PartKey, GameControllerIntermediateGameItem[]][]
    ).find(
      ([, partItems]) =>
        partItems.find((item) => item.game.gameId === gameId) !== undefined,
    ) ?? [];
  if (partIndex === undefined || partGameItems === undefined) {
    throw new Error('invalid game id');
  }

  // console.log('target part');
  // console.log(partGameItems);

  // download the game itself or all pairing game within the same part
  const gameItem = partGameItems.find((item) => item.game.gameId === gameId)!; // should not be undefined since find in above
  const part = gameCollections[0]?.parts[partIndex]; // FIXME: part may not exist in first part?

  return {
    intermediate: {
      gameItems: partGameItems.filter(
        (item) => item === gameItem,
        // || (gameItem.game.gameType === GameType.Pairing &&
        //   item.game.gameType === GameType.Pairing),
      ),
      useMic: isSpeaking(), // isSpeaking(gameItem.game.gameType),
      useSpeechRecognition: false,
      gameCollectionId: gameItem.gameCollectionId,
      partIndex: partIndex,
      i18nPartInfo: part?.i18nPartInfo,
      icon: part?.icon,
      partTheme: Object.keys(items).indexOf(partIndex) % 6,
      slideshow: gameItem.slideshow,
      gameType: gameItem.game.gameType,
      unlockFee: gameItem.unlockFee,
      gameReward: gameItem.gameReward,
      gameAnswerSlots: gameItem.game.answerSlots,
      slideshowReward: gameItem.slideshowReward,
    },
  };
};

// export type PartChallengeParams = {
//   classroomId: string;
//   gameSetId: string;
//   partIndex: PartKey;
//   gameCollectionIds?: string[];
//   postId?: string;
//   reportCase?: string;
// };

// export type PartChallengeIntermediate = GameControllerIntermediate & {
//   i18nPartInfo: I18nPartInfo | undefined;
//   icon: Icon | undefined;
// };

// export const preparePartChallengeIntermediate: PrepareIntermediateFn<
//   PartChallengeParams,
//   PartChallengeIntermediate
// > = async (
//   { userId },
//   { classroomId, gameSetId, partIndex, gameCollectionIds = [] },
// ) => {
//   const { isFeatureAvailable } = useSystemStore.getState();
//   const { getSpace, getMembership, getAlbum } = useSpaceStore.getState();
//   const { getGameCollection, fetchGameCollections } = useGameStore.getState();

//   const speakingAvailable = isFeatureAvailable('speakingGames');
//   const isMultiLang: boolean = getSpace(classroomId)?.isMultiLang ?? false;
//   const membership = getMembership(classroomId, userId);

//   const album = getAlbum(gameSetId);

//   const content = album?.content;
//   if (
//     content === undefined ||
//     content === null ||
//     ![AlbumContentType.Both, AlbumContentType.Game].includes(content.type)
//   ) {
//     throw new Error('invalid content type');
//   }

//   const partChallenge = content.partChallenges?.[partIndex];
//   if (partChallenge === undefined) {
//     throw new Error('invalid part');
//   }

//   // download all game collection in gameCollectionIds first
//   if (gameCollectionIds.length !== content.gameCollectionIds.length) {
//     // console.log('fetchGameCollections');
//     await fetchGameCollections('gamePlay', content.gameCollectionIds);
//     return { gameCollectionIds: content.gameCollectionIds };
//   }

//   // throw if downloaded game collection but cannot get from repository
//   const gcs = gameCollectionIds.map(getGameCollection);
//   if (gcs.some((gameCollection) => !gameCollection)) {
//     throw new Error('game collection not found');
//   }
//   const gameCollections = gcs as GameCollection[];

//   // find game items within the same part
//   const { totalCount, pairingCount } = partChallenge;

//   const totalGameItems: GameControllerIntermediateGameItem[] = shuffle(
//     (
//       mergeWith(
//         {},
//         ...gameCollections.map((gameCollection) =>
//           gameCollection.getPartItemMap(
//             ({ game }) =>
//               game !== undefined &&
//               isGameType(game.gameType, {
//                 pairing: false,
//                 speaking: speakingAvailable ? undefined : false,
//                 manuallyEvaluated: false,
//               }) &&
//               (!isMultiLang || membership?.hasLocale(game.defaultLocale)),
//           ),
//         ),
//         (
//           objValue: GameControllerIntermediateGameItem[],
//           srcValue: GameControllerIntermediateGameItem[],
//         ) => (Array.isArray(objValue) ? objValue.concat(srcValue) : undefined),
//       ) as Record<string, GameControllerIntermediateGameItem[]>
//     )[partIndex] ?? [],
//   ).slice(0, totalCount);
//   const pairingGameItems: GameControllerIntermediateGameItem[] = shuffle(
//     (
//       mergeWith(
//         {},
//         ...gameCollections.map((gameCollection) =>
//           gameCollection.getPartItemMap(
//             ({ game }) =>
//               game !== undefined &&
//               game.gameType === GameType.Pairing &&
//               (!isMultiLang || membership?.hasLocale(game.defaultLocale)),
//           ),
//         ),
//         (
//           objValue: GameControllerIntermediateGameItem[],
//           srcValue: GameControllerIntermediateGameItem[],
//         ) => (Array.isArray(objValue) ? objValue.concat(srcValue) : undefined),
//       ) as Record<string, GameControllerIntermediateGameItem[]>
//     )[partIndex] ?? [],
//   ).map((item, i) => ({
//     ...item,
//     fake: i >= (pairingCount ?? Infinity),
//   }));

//   const gameItems = totalGameItems.concat(pairingGameItems);
//   if (gameItems.length === 0) {
//     throw new Error('invalid part');
//   }

//   // console.log('target part');
//   // console.log(gameItems);

//   const part = gameCollections[0]?.parts[partIndex]; // FIXME: part may not exist in first part?

//   return {
//     intermediate: {
//       gameItems,
//       i18nPartInfo: part?.i18nPartInfo,
//       icon: part?.icon,
//       useMic: gameItems.some((item) => isSpeaking(item.game.gameType)),
//       useSpeechRecognition: false,
//     },
//   };
// };

// export type MiniChallengeParams = {
//   classroomId: string;
//   gameSetId: string;
//   partIndex: PartKey;
//   partMiniIndex: number;
//   gameCollectionIds?: string[];
//   postId?: string;
//   reportCase?: string;
// };

// export type MiniChallengeIntermediate = GameControllerIntermediate & {
//   i18nPartInfo: I18nPartInfo | undefined;
//   icon: Icon | undefined;
// };

// export const prepareMiniChallengeIntermediate: PrepareIntermediateFn<
//   MiniChallengeParams,
//   MiniChallengeIntermediate
// > = async (
//   { userId },
//   { classroomId, gameSetId, partIndex, gameCollectionIds = [], partMiniIndex },
// ) => {
//   const { isFeatureAvailable } = useSystemStore.getState();
//   const { getSpace, getMembership, getAlbum } = useSpaceStore.getState();
//   const { getGameCollection, fetchGameCollections } = useGameStore.getState();

//   const speakingAvailable = isFeatureAvailable('speakingGames');
//   const isMultiLang: boolean = getSpace(classroomId)?.isMultiLang ?? false;
//   const membership = getMembership(classroomId, userId);

//   const album = getAlbum(gameSetId);

//   const content = album?.content;
//   if (
//     content === undefined ||
//     content === null ||
//     ![AlbumContentType.Both, AlbumContentType.Game].includes(content.type)
//   ) {
//     throw new Error('invalid content type');
//   }

//   const miniChallenge = content.miniChallenges?.[partIndex];
//   if (miniChallenge === undefined) {
//     throw new Error('invalid part');
//   }

//   // download all game collection in gameCollectionIds first
//   if (gameCollectionIds.length !== content.gameCollectionIds.length) {
//     // console.log('fetchGameCollections');
//     await fetchGameCollections('gamePlay', content.gameCollectionIds);
//     return { gameCollectionIds: content.gameCollectionIds };
//   }

//   // throw if downloaded game collection but cannot get from repository
//   const gcs = gameCollectionIds.map(getGameCollection);
//   if (gcs.some((gameCollection) => !gameCollection)) {
//     throw new Error('game collection not found');
//   }
//   const gameCollections = gcs as GameCollection[];

//   // find game items within the same part
//   const { interval } = miniChallenge;
//   const from = partMiniIndex * interval;

//   // get the corresponding games in interval
//   const allGameItems = shuffle(
//     (
//       (
//         mergeWith(
//           {},
//           ...gameCollections.map((gameCollection) =>
//             gameCollection.getPartItemMap(
//               ({ game }) =>
//                 game !== undefined &&
//                 isGameType(game.gameType, {
//                   speaking: speakingAvailable ? undefined : false,
//                   manuallyEvaluated: false,
//                 }) &&
//                 (!isMultiLang || membership?.hasLocale(game.defaultLocale)),
//             ),
//           ),
//           (
//             objValue: GameControllerIntermediateGameItem[],
//             srcValue: GameControllerIntermediateGameItem[],
//           ) =>
//             Array.isArray(objValue) ? objValue.concat(srcValue) : undefined,
//         ) as Record<string, GameControllerIntermediateGameItem[]>
//       )[partIndex] ?? []
//     ).slice(from, from + interval),
//   );

//   const totalGameItems: GameControllerIntermediateGameItem[] = [];
//   const pairingGameItems: GameControllerIntermediateGameItem[] = [];

//   // group the pairing games
//   allGameItems.forEach((item) => {
//     if (item.game.gameType === GameType.Pairing) {
//       // XXX: add fake pairing game pair from previous or subsequent?
//       pairingGameItems.push(item);
//     } else {
//       totalGameItems.push(item);
//     }
//   });

//   // should have the same number of game items
//   const gameItems = totalGameItems.concat(pairingGameItems);
//   if (gameItems.length !== interval) {
//     throw new Error('invalid part');
//   }

//   // console.log('target part');
//   // console.log(gameItems);

//   const part = gameCollections[0]?.parts[partIndex]; // FIXME: part may not exist in first part?

//   return {
//     intermediate: {
//       gameItems,
//       i18nPartInfo: part?.i18nPartInfo,
//       icon: part?.icon,
//       useMic: gameItems.some((item) => isSpeaking(item.game.gameType)),
//       useSpeechRecognition: false,
//     },
//   };
// };

// export type ChallengeParams = {
//   classroomId: string;
//   gameSetId: string;
//   gameCollectionIds?: string[];
//   postId?: string;
//   reportCase?: string;
// };

// export type ChallengeIntermediate = GameControllerIntermediate;

// export const prepareChallengeIntermediate: PrepareIntermediateFn<
//   ChallengeParams,
//   ChallengeIntermediate
// > = async ({ userId }, { classroomId, gameSetId, gameCollectionIds = [] }) => {
//   const { isFeatureAvailable } = useSystemStore.getState();
//   const { getSpace, getMembership, getAlbum } = useSpaceStore.getState();
//   const { getGameCollection, fetchGameCollections } = useGameStore.getState();

//   const speakingAvailable = isFeatureAvailable('speakingGames');
//   const isMultiLang: boolean = getSpace(classroomId)?.isMultiLang ?? false;
//   const membership = getMembership(classroomId, userId);

//   const album = getAlbum(gameSetId);

//   const challenge = album?.challenge;
//   if (
//     challenge === undefined ||
//     challenge === null ||
//     ![
//       AlbumChallengeType.Both,
//       AlbumChallengeType.Challenge,
//       AlbumChallengeType.Practice,
//     ].includes(challenge.type)
//   ) {
//     throw new Error('invalid challenge type');
//   }

//   // throw if downloaded game collection but cannot get from repository
//   const gcs = gameCollectionIds.map(getGameCollection);
//   if (gcs.some((gameCollection) => !gameCollection)) {
//     throw new Error('game collection not found');
//   }
//   const gameCollections = gcs as GameCollection[];

//   if (challenge.gameCollectionIds) {
//     // merge existing game items by parts
//     const totalGameItems = mergeWith(
//       {},
//       ...gameCollections.map((gameCollection) =>
//         gameCollection.getPartItemMap(
//           ({ game }) =>
//             game !== undefined &&
//             isGameType(game.gameType, {
//               pairing: false,
//               speaking: speakingAvailable ? undefined : false,
//               manuallyEvaluated: false,
//             }) &&
//             (!isMultiLang || membership?.hasLocale(game.defaultLocale)),
//         ),
//       ),
//       (
//         objValue: GameControllerIntermediateGameItem[],
//         srcValue: GameControllerIntermediateGameItem[],
//       ) => (Array.isArray(objValue) ? objValue.concat(srcValue) : undefined),
//     ) as Record<string, GameControllerIntermediateGameItem[]>;
//     const pairingGameItems = mergeWith(
//       {},
//       ...gameCollections.map((gameCollection) =>
//         gameCollection.getPartItemMap(
//           ({ game }) =>
//             game !== undefined &&
//             game.gameType === GameType.Pairing &&
//             (!isMultiLang || membership?.hasLocale(game.defaultLocale)),
//         ),
//       ),
//       (
//         objValue: GameControllerIntermediateGameItem[],
//         srcValue: GameControllerIntermediateGameItem[],
//       ) => (Array.isArray(objValue) ? objValue.concat(srcValue) : undefined),
//     ) as Record<string, GameControllerIntermediateGameItem[]>;

//     // console.log('source');
//     // console.log(totalGameItems);
//     // console.log(pairingGameItems);

//     // filter based on part counts
//     const canFetchMore =
//       gameCollections.length < challenge.gameCollectionIds.length;
//     let missing = 0;
//     let aggregateTotalGameItems: GameControllerIntermediateGameItem[] = [];
//     let aggregatePairingGameItems: GameControllerIntermediateGameItem[] = [];
//     const aggregateParts = new Set<PartKey>();

//     Object.entries(challenge.parts ?? {}).forEach(
//       ([_partIndex, { totalCount, pairingCount }]) => {
//         const partIndex = _partIndex as PartKey;
//         if (partIndex !== 'root') {
//           aggregateParts.add(partIndex);

//           const partTotalGameItems = totalGameItems[partIndex] ?? [];
//           const partPairingGameItems = pairingGameItems[partIndex] ?? [];

//           if (totalCount === undefined && pairingCount === undefined) {
//             if (canFetchMore) {
//               missing = Infinity;
//             } else {
//               aggregateTotalGameItems =
//                 aggregateTotalGameItems.concat(partTotalGameItems);
//               aggregatePairingGameItems =
//                 aggregatePairingGameItems.concat(partPairingGameItems);
//             }
//           } else {
//             const totalLimit = totalCount ?? 0;
//             const pairingLimit = pairingCount ?? 0;

//             if (
//               canFetchMore &&
//               (partTotalGameItems.length < totalLimit ||
//                 partPairingGameItems.length < pairingLimit)
//             ) {
//               missing +=
//                 Math.max(0, totalLimit - partTotalGameItems.length) +
//                 Math.max(0, pairingLimit - partPairingGameItems.length);
//             } else {
//               aggregateTotalGameItems = aggregateTotalGameItems.concat(
//                 shuffle(partTotalGameItems).slice(0, totalLimit),
//               );
//               aggregatePairingGameItems = aggregatePairingGameItems.concat(
//                 shuffle(partPairingGameItems).map((item, i) => ({
//                   ...item,
//                   fake: i >= (pairingCount ?? Infinity),
//                 })),
//               );
//             }
//           }
//         }
//       },
//     );

//     // console.log('used part counts');
//     // console.log(aggregateTotalGameItems);
//     // console.log(aggregatePairingGameItems);

//     // add remaining items without part counts
//     aggregateTotalGameItems = aggregateTotalGameItems.concat(
//       (
//         Object.entries(totalGameItems) as [
//           PartKey,
//           GameControllerIntermediateGameItem[],
//         ][]
//       ).flatMap(([partIndex, items]) =>
//         partIndex !== 'root' && !aggregateParts.has(partIndex) ? items : [],
//       ),
//     );
//     aggregatePairingGameItems = aggregatePairingGameItems.concat(
//       (
//         Object.entries(pairingGameItems) as [
//           PartKey,
//           GameControllerIntermediateGameItem[],
//         ][]
//       ).flatMap(([partIndex, items]) =>
//         partIndex !== 'root' && !aggregateParts.has(partIndex) ? items : [],
//       ),
//     );

//     // console.log('added remaining');
//     // console.log(aggregateTotalGameItems);
//     // console.log(aggregatePairingGameItems);

//     // apply root counts
//     const { totalCount, pairingCount } = challenge.parts?.root ?? {};
//     if (totalCount === undefined && pairingCount === undefined) {
//       if (canFetchMore) {
//         missing = Infinity;
//       }
//     } else {
//       const totalLimit = totalCount ?? 0;
//       const pairingLimit = pairingCount ?? 0;

//       if (
//         canFetchMore &&
//         (aggregateTotalGameItems.length < totalLimit ||
//           aggregatePairingGameItems.length < pairingLimit)
//       ) {
//         missing +=
//           Math.max(0, totalLimit - aggregateTotalGameItems.length) +
//           Math.max(0, pairingLimit - aggregatePairingGameItems.length);
//       } else {
//         aggregateTotalGameItems = shuffle(aggregateTotalGameItems).slice(
//           0,
//           totalLimit,
//         );
//         aggregatePairingGameItems = shuffle(aggregatePairingGameItems).slice(
//           0,
//           pairingLimit,
//         );
//       }
//     }

//     // console.log('applied root count');
//     // console.log(aggregateTotalGameItems);
//     // console.log(aggregatePairingGameItems);

//     // download the game collections if not enough game, otherwise return game items
//     if (missing === Infinity) {
//       // console.log('missing = Infinity');
//       await fetchGameCollections('gamePlay', challenge.gameCollectionIds);
//       return { gameCollectionIds: challenge.gameCollectionIds };
//     }

//     if (missing) {
//       // console.log(`missing = ${missing}`);
//       const newIds = shuffle(
//         challenge.gameCollectionIds.filter(
//           (id) => !gameCollectionIds.includes(id),
//         ),
//       ).slice(0, Math.max(10, missing));
//       await fetchGameCollections('gamePlay', newIds);
//       return { gameCollectionIds: gameCollectionIds.concat(newIds) };
//     }

//     aggregateTotalGameItems = shuffle(aggregateTotalGameItems);
//     aggregatePairingGameItems = shuffle(aggregatePairingGameItems);
//     const gameItems = aggregateTotalGameItems.concat(aggregatePairingGameItems);

//     return {
//       intermediate: {
//         gameItems,
//         useMic: gameItems.some((item) => isSpeaking(item.game.gameType)),
//         useSpeechRecognition: false,
//       },
//     };
//   } else if (challenge.categories) {
//     type AggregateItem = GameControllerIntermediateGameItem & {
//       fake?: boolean;
//       category?: number;
//     };
//     type AggregateInfo = {
//       gameCollectionIds: Set<string>;
//       aggregateTotalGameItems: AggregateItem[];
//       aggregatePairingGameItems: AggregateItem[];
//     };
//     // handle data by section since there are the highest level counts
//     const categories = challenge.categories
//       .flatMap(({ sections }, index) =>
//         sections.map(
//           ({ gameCollections: collections, totalCount, pairingCount }) => {
//             const section = collections.reduce<AggregateInfo>(
//               (acc, c) => {
//                 if (gameCollectionIds.includes(c.gameCollectionId)) {
//                   // find game items for game collection
//                   const gameCollection = gameCollections.find(
//                     (gc) => gc.gameCollectionId === c.gameCollectionId,
//                   );
//                   const totalGameItems =
//                     gameCollection?.getPartItemMap<GameControllerIntermediateGameItem>(
//                       ({ game }) =>
//                         game !== undefined &&
//                         isGameType(game.gameType, {
//                           pairing: false,
//                           speaking: speakingAvailable ? undefined : false,
//                           manuallyEvaluated: false,
//                         }) &&
//                         (!isMultiLang ||
//                           membership?.hasLocale(game.defaultLocale)),
//                     ) ?? {};
//                   const pairingGameItems =
//                     gameCollection?.getPartItemMap<GameControllerIntermediateGameItem>(
//                       ({ game }) =>
//                         game !== undefined &&
//                         game.gameType === GameType.Pairing &&
//                         (!isMultiLang ||
//                           membership?.hasLocale(game.defaultLocale)),
//                     ) ?? {};

//                   // filter by part counts if any parts specified, otherwise use all items
//                   let aggregateTotalGameItems: AggregateItem[] = [];
//                   let aggregatePairingGameItems: AggregateItem[] = [];
//                   let useAllParts = true;

//                   (Object.entries(c.parts) as [PartKey, PartCount][]).forEach(
//                     ([
//                       partIndex,
//                       {
//                         totalCount: partTotalCount,
//                         pairingCount: partPairingCount,
//                       },
//                     ]) => {
//                       if (partIndex !== 'root') {
//                         useAllParts = false;

//                         const partTotalGameItems =
//                           totalGameItems[partIndex] ?? [];
//                         const partPairingGameItems =
//                           pairingGameItems[partIndex] ?? [];
//                         if (
//                           partTotalCount === undefined &&
//                           partPairingCount === undefined
//                         ) {
//                           aggregateTotalGameItems =
//                             aggregateTotalGameItems.concat(partTotalGameItems);
//                           // add "fake" to all to ensure sorting later
//                           aggregatePairingGameItems =
//                             aggregatePairingGameItems.concat(
//                               partPairingGameItems.map((item) => ({
//                                 ...item,
//                                 fake: false,
//                               })),
//                             );
//                         } else {
//                           aggregateTotalGameItems =
//                             aggregateTotalGameItems.concat(
//                               shuffle(partTotalGameItems).slice(
//                                 0,
//                                 partTotalCount ?? 0,
//                               ),
//                             );
//                           // add "fake" to all to ensure sorting later
//                           aggregatePairingGameItems =
//                             aggregatePairingGameItems.concat(
//                               shuffle(partPairingGameItems).map((item, i) => ({
//                                 ...item,
//                                 fake: i >= (partPairingCount ?? 0),
//                               })),
//                             );
//                         }
//                       }
//                     },
//                   );

//                   if (useAllParts) {
//                     aggregateTotalGameItems = Object.entries(
//                       totalGameItems,
//                     ).flatMap(([, items]) => items);
//                     // add "fake" to all to ensure sorting later
//                     aggregatePairingGameItems = Object.entries(
//                       pairingGameItems,
//                     ).flatMap(([, items]) =>
//                       items.map((item) => ({ ...item, fake: false })),
//                     );
//                   }

//                   // apply root counts
//                   const {
//                     totalCount: rootTotalCount,
//                     pairingCount: rootPairingCount,
//                   } = c.parts.root ?? {};
//                   if (
//                     rootTotalCount !== undefined ||
//                     rootPairingCount !== undefined
//                   ) {
//                     aggregateTotalGameItems = shuffle(
//                       aggregateTotalGameItems,
//                     ).slice(0, rootTotalCount ?? 0);
//                     aggregatePairingGameItems = orderBy(
//                       shuffle(aggregatePairingGameItems),
//                       'fake',
//                       'asc',
//                     ).map((item, i) => ({
//                       ...item,
//                       fake: item.fake || i >= (rootPairingCount ?? Infinity),
//                     }));
//                   }

//                   // console.log(`collection ${c.gameCollectionId} items`);
//                   // console.log(aggregateTotalGameItems);

//                   acc.aggregateTotalGameItems =
//                     acc.aggregateTotalGameItems.concat(aggregateTotalGameItems);
//                   acc.aggregatePairingGameItems =
//                     acc.aggregatePairingGameItems.concat(
//                       aggregatePairingGameItems,
//                     );
//                 } else {
//                   // return game collection id in case not enough game items
//                   acc.gameCollectionIds.add(c.gameCollectionId);
//                 }
//                 return acc;
//               },
//               {
//                 gameCollectionIds: new Set(),
//                 aggregateTotalGameItems: [],
//                 aggregatePairingGameItems: [],
//               },
//             );

//             // console.log('section');

//             // apply section counts
//             let { aggregateTotalGameItems, aggregatePairingGameItems } =
//               section;

//             const canFetchMore = section.gameCollectionIds.size > 0;
//             let missing = 0;

//             if (totalCount === undefined && pairingCount === undefined) {
//               if (canFetchMore) {
//                 missing = Infinity;
//               }
//             } else {
//               const totalLimit = totalCount ?? 0;
//               const pairingLimit = pairingCount ?? 0;

//               if (
//                 canFetchMore &&
//                 (aggregateTotalGameItems.length < totalLimit ||
//                   aggregatePairingGameItems.length < pairingLimit)
//               ) {
//                 missing +=
//                   Math.max(0, totalLimit - aggregateTotalGameItems.length) +
//                   Math.max(0, pairingLimit - aggregatePairingGameItems.length);
//               } else {
//                 aggregateTotalGameItems = shuffle(
//                   aggregateTotalGameItems,
//                 ).slice(0, totalLimit);
//                 aggregatePairingGameItems = orderBy(
//                   shuffle(aggregatePairingGameItems),
//                   'fake',
//                   'asc',
//                 ).map((item, i) => ({
//                   ...item,
//                   fake: item.fake || i >= (pairingCount ?? Infinity),
//                 }));
//               }
//             }

//             // return the game collections to download more if not enough game, otherwise return game items
//             // console.log(`missing = ${missing}`);
//             if (missing) {
//               return {
//                 gameCollectionIds: shuffle([
//                   ...section.gameCollectionIds,
//                 ]).slice(0, missing),
//               };
//             }
//             return {
//               aggregateTotalGameItems: aggregateTotalGameItems.map((item) => ({
//                 ...item,
//                 category: index,
//               })),
//               aggregatePairingGameItems: aggregatePairingGameItems.map(
//                 (item) => ({ ...item, category: index }),
//               ),
//             };
//           },
//         ),
//       )
//       .reduce<AggregateInfo>(
//         (
//           acc,
//           {
//             gameCollectionIds: ids = [],
//             aggregateTotalGameItems = [],
//             aggregatePairingGameItems = [],
//           },
//         ) => {
//           ids.forEach((id) => {
//             acc.gameCollectionIds.add(id);
//           });
//           acc.aggregateTotalGameItems = acc.aggregateTotalGameItems.concat(
//             aggregateTotalGameItems,
//           );
//           acc.aggregatePairingGameItems = acc.aggregatePairingGameItems.concat(
//             aggregatePairingGameItems,
//           );
//           return acc;
//         },
//         {
//           gameCollectionIds: new Set(),
//           aggregateTotalGameItems: [],
//           aggregatePairingGameItems: [],
//         },
//       );

//     // console.log('categories');
//     // console.log(categories);

//     if (categories.gameCollectionIds.size > 0) {
//       await fetchGameCollections('gamePlay', [...categories.gameCollectionIds]);
//       return {
//         gameCollectionIds: gameCollectionIds.concat([
//           ...categories.gameCollectionIds,
//         ]),
//       };
//     }
//     categories.aggregateTotalGameItems = shuffle(
//       categories.aggregateTotalGameItems,
//     );
//     categories.aggregatePairingGameItems = shuffle(
//       categories.aggregatePairingGameItems,
//     );
//     const gameItems = categories.aggregateTotalGameItems.concat(
//       categories.aggregatePairingGameItems,
//     );
//     if (gameItems.length === 0) {
//       throw new Error('invalid challenge');
//     }

//     return {
//       intermediate: {
//         gameItems,
//         useMic: gameItems.some((item) => isSpeaking(item.game.gameType)),
//         useSpeechRecognition: false,
//       },
//     };
//   }
//   throw new Error('not implemented');
// };

export type ChallengeParams = {
  classroomId: string;
  gameSetId: string;
  gameId: never;
  challengeIndex: number | 'super' | 'comprehensive';
  gameCollectionIds?: string[];
  isAssistedGame: never;
  isRevisionGame: never;
  // postId?: string;
  // reportCase?: string;
};

export type ChallengeIntermediate = GameControllerIntermediate;

export const prepareChallengeIntermediate: PrepareIntermediateFn<
  ChallengeParams,
  ChallengeIntermediate
> = async (
  { userId },
  { gameSetId, challengeIndex, gameCollectionIds = [] },
) => {
  const { getAlbum } = useSpaceStore.getState();
  const { getGameCollection, fetchGameCollectionsByIds } =
    useGameStore.getState();
  const { getProfile } = useUserStore.getState();

  let gameCollections;
  const userProfile = getProfile(userId);

  // assuming game set already downloaded
  const album = getAlbum(gameSetId);

  const content = album?.content;
  if (
    content === undefined ||
    content === null ||
    ![AlbumContentType.Both, AlbumContentType.Game].includes(content.type)
  ) {
    throw new Error('invalid content type');
  }

  // download all game collection in gameCollectionIds first
  if (gameCollectionIds.length !== content.gameCollectionIds.length) {
    await fetchGameCollectionsByIds({
      fieldName: 'gamePlay',
      gameCollectionIds: content.gameCollectionIds,
    });
    return { gameCollectionIds: content.gameCollectionIds };
  }

  gameCollections = gameCollectionIds.map(getGameCollection);

  // throw if downloaded game collection but cannot get from repository
  if (gameCollections.some((gameCollection) => !gameCollection)) {
    throw new Error('game collection not found');
  }
  gameCollections = gameCollections as GameCollection[];

  // find game items within the same part
  let items = gameCollections.flatMap((gameCollection) =>
    gameCollection.getPartItemArray((item) => item.game),
  ) as GameControllerIntermediateGameItem[];

  const stations = album!.content!.path.flatMap((path) => path.stations);
  let start = 0;
  let end = items.length;
  if (typeof challengeIndex === 'number') {
    end = 0;
    let pathChallengeCount = 0;
    for (let i = 0; i < stations.length; i += 1) {
      if (stations[i]?.type === StationType.PathChallenge) {
        pathChallengeCount += 1;
      } else if (stations[i]?.type === StationType.Game) {
        if (pathChallengeCount < challengeIndex) {
          start += 1;
        }
        if (pathChallengeCount <= challengeIndex) {
          end += 1;
        }
      }
    }
  } else {
    end = Math.min(
      end,
      stations.filter((station) => station.type === StationType.Game).length,
    );
  }

  items = items.slice(start, end).filter((item) => item.game.isForChallenge);

  customLog('source', userProfile);
  customLog(JSON.stringify(items), userProfile);
  customLog(challengeIndex, userProfile);

  let limit: number | undefined = 3;
  if (challengeIndex === 'super') {
    limit = 5;
  } else if (challengeIndex === 'comprehensive') {
    limit = undefined;
  }
  items = shuffle(items).slice(0, limit);
  if (items.length === 0) {
    throw new Error('no games for challenge');
  }

  customLog('random selection', userProfile);
  customLog(items, userProfile);

  return {
    intermediate: {
      gameItems: items,
      useMic: isSpeaking(), // isSpeaking(gameItem.game.gameType),
      useSpeechRecognition: false,
    },
  };
};
